import React, { useState, useRef, useEffect, useCallback } from 'react'
import SunEditor from 'suneditor-react'
import 'suneditor/dist/css/suneditor.min.css'
import styles from './styles'
import {
  QuestionFieldNames,
  AnswerItemType,
  QuestionEditorHeights,
} from 'src/model/card'
import { withStyles } from '@material-ui/core'
import {
  blockquote,
  align,
  font,
  fontSize,
  fontColor,
  hiliteColor,
  horizontalRule,
  list,
  table,
  formatBlock,
  lineHeight,
  template,
  paragraphStyle,
  textStyle,
  link,
  image,
  audio,
  math,
  imageGallery,
} from 'suneditor/src/plugins'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import { editorLoading } from 'src/reducers/card/action'
import { useSelector, useDispatch } from 'react-redux'
import { isMobile } from 'react-device-detect'
import { mediaImageUploadAction } from 'src/reducers/media-image/action'
import URL from 'src/api/url'
import { useLocation } from 'react-router-dom'

const CoverSuneditor = props => {
  const { classes } = props
  const dispatch = useDispatch()
  const location = useLocation()
  const { execute, update, exists, value } = useCourseSettings('general')
  const course = useSelector(state => state.course.course)
  const editorLoadingState = useSelector(state => state.card.editorLoading)
  const editor = useRef()

  // Track both current content and loading state
  const [currentContent, setCurrentContent] = useState('')
  const searchParams = new URLSearchParams(location.search)
  const cardId = searchParams.get('page')

  // Generate unique key for editor instance
  const editorKey = `suneditor-${course?.id}-${cardId}`

  // Handle content initialization and updates
  useEffect(() => {
    if (value?.coverPageText !== undefined) {
      setCurrentContent(value.coverPageText)

      // Force editor content update if editor instance exists
      if (editor.current) {
        editor.current.setContents(value.coverPageText)
      }
    }
  }, [value?.coverPageText, course?.id])

  // Reset loading state when course changes
  useEffect(() => {
    let timeoutId = null
    if (editorLoadingState) {
      timeoutId = setTimeout(() => {
        dispatch(editorLoading())
      }, 2000)
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [course?.id, dispatch, editorLoadingState])

  // Memoized save handler
  const handleSaveCourseSettings = useCallback(
    newDescription => {
      // Only save if content has actually changed
      if (newDescription !== currentContent) {
        dispatch(editorLoading())

        const variables = {
          ...value,
          coverPageText: newDescription,
        }

        const callback = () => {
          const timeoutId = setTimeout(() => {
            dispatch(editorLoading())
            setCurrentContent(newDescription)
          }, 2000)
        }

        if (!exists) {
          execute(variables, callback)
        } else {
          update(variables, callback)
        }
      }
    },
    [dispatch, execute, update, exists, value, currentContent],
  )

  const getSunEditorInstance = useCallback(sunEditor => {
    editor.current = sunEditor
  }, [])

  const onChange = useCallback(value => {
    const cleanContent = value.replace('+++', '')
    setCurrentContent(cleanContent)
  }, [])

  // Memoize editor options
  const editorOptions = React.useMemo(
    () => ({
      key: `editorOptions-${course?.id}`,
      mode: 'classic',
      formats: ['h1', 'h2', 'p'],
      placeholder: 'Cover Text',
      height: 'unset',
      marginTop: '12px',
      minHeight: QuestionEditorHeights[AnswerItemType.INFORM]
        ? isMobile
          ? QuestionEditorHeights[AnswerItemType.INFORM].mobile
          : QuestionEditorHeights[AnswerItemType.INFORM].desktop
        : 80,
      addTagsWhitelist: 'file-embed|google-maps|div',
      resizingBar: false,
      buttonList: [
        [
          ':t-Format Text-default.more_text',
          'formatBlock',
          'bold',
          'italic',
          'underline',
          'fontColor',
          'hiliteColor',
        ],
        ['align'],
        ['list'],
        ['image'],
        ['link'],
        ['removeFormat'],
        ['codeView'],
      ],
      pasteTagsWhitelist: 'p|h1|h2|h3',
      popupDisplay: 'local',
      plugins: [
        blockquote,
        align,
        font,
        fontSize,
        fontColor,
        hiliteColor,
        horizontalRule,
        list,
        table,
        formatBlock,
        lineHeight,
        template,
        paragraphStyle,
        textStyle,
        link,
        image,
        audio,
        math,
        imageGallery,
      ],
      attributesWhitelist: {
        iframe:
          'dir|class|src|width|height|title|allow|allowfullscreen|referrerpolicy|aspectratio|id|frameborder|iframe|className|data-.+',
        div:
          'dir|style|class|src|width|height|title|allow|allowfullscreen|referrerpolicy|aspectratio|id|frameborder|iframe|className|data-.+',
      },
      stickyToolbar: '65px',
    }),
    [course?.id],
  )

  // Memoize image upload handler
  const handleImageUpload = useCallback(
    (files, info, uploadHandler) => {
      dispatch(
        mediaImageUploadAction(files[0], '', result => {
          const response = {
            result: [
              {
                url: `${URL.MEDIA}${result.url}`,
                name: result.name,
                size: 100,
              },
            ],
          }
          uploadHandler(response)
        }),
      )
    },
    [dispatch],
  )

  if (!course || !value || cardId !== 'cover') {
    return null
  }

  return (
    <SunEditor
      getSunEditorInstance={getSunEditorInstance}
      key={editorKey}
      defaultValue={currentContent}
      onChange={onChange}
      setContents={currentContent}
      onBlur={(event, editorContents) => {
        handleSaveCourseSettings(editorContents)
      }}
      className={classes.textArea}
      setDefaultStyle="text-align: left"
      onImageUploadBefore={handleImageUpload}
      setOptions={editorOptions}
    />
  )
}

export default withStyles(styles)(CoverSuneditor)
